import { REACTIONS, ReactionType } from "@/src/models/post.model";
import { animateBounce } from "@/src/utils/animation.utils";
import { ArrowForwardSquare, MessageText } from "iconsax-react-nativejs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Animated, Platform, Pressable, Text, View } from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import ReactionPicker from "../reactions/reaction-picker";
import { LikeButton } from "./bottom-buttons/bottom-buttons";

type Props = Readonly<{
  userReaction?: ReactionType;
  reactionCounts?: Partial<Record<Exclude<ReactionType, null>, number>>;
  reactionTotal?: number;
  commentCount?: number;
  saveCount?: number;
  onReact?: (reaction: ReactionType | null) => void;
  onComment?: () => void;
  onShare?: () => void;
  onSave?: () => void;
  onReactorCount?: () => void;
}>;

const PostActions = ({
  userReaction = null,
  reactionCounts = {},
  reactionTotal = 0,
  commentCount = 0,
  saveCount = 0,
  onReact,
  onComment,
  onShare,
  onSave,
  onReactorCount
}: Props) => {
  const { themeVars } = useTheme();
  const { t } = useTranslation();

  const [showPicker, setShowPicker] = useState(false);
  const [currentReaction, setCurrentReaction] =
    useState<ReactionType>(userReaction);

  useEffect(() => {
    setCurrentReaction(userReaction);
  }, [userReaction]);

  const likeAnim = useRef(new Animated.Value(1)).current;
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    };
  }, []);

  const topReactions = useMemo(
    () =>
      Object.entries(reactionCounts)
        .filter(([, count]) => (count ?? 0) > 0)
        .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
        .slice(0, 3)
        .map(([id]) => REACTIONS.find((r) => r.id === id))
        .filter(Boolean) as typeof REACTIONS,
    [reactionCounts],
  );

  const handleLikePress = useCallback(() => {
    animateBounce(likeAnim);
    const reaction = currentReaction ? null : "like";
    setCurrentReaction(reaction);
    onReact?.(reaction);
  }, [currentReaction, likeAnim, onReact]);

  const handlePickerSelect = useCallback(
    (reaction: ReactionType) => {
      animateBounce(likeAnim);
      setCurrentReaction(reaction);
      setShowPicker(false);
      onReact?.(reaction);
    },
    [likeAnim, onReact],
  );

  const handleHoverIn = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setShowPicker(true), 250);
  }, []);

  const handleHoverOut = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setShowPicker(false), 300);
  }, []);

  return (
    <View className="border-ash-3">
      {/* Stats row */}
      <View className="flex-row justify-between items-center px-6 py-2">
        <Pressable
          className="flex-row items-center gap-1 hover:bg-ash-3 hover:rounded-lg"
          onPress={onReactorCount}
        >
          {topReactions.map((rxn, index) => (
            <Text
              key={rxn.id}
              style={{
                fontSize: 16,
                marginLeft: index === 0 ? 0 : -12,
                zIndex: topReactions.length - index,
              }}
              className="bg-chalk rounded-full"
            >
              {rxn.emoji}
            </Text>
          ))}
          <Text className="text-sm text-ash-2">
            {reactionTotal === 0 ? `0 ${t("LABEL_LIKES")}` : reactionTotal}
          </Text>
        </Pressable>
        <Text className="text-onyx text-xs">
          {commentCount}{" "}
          {commentCount === 1 ? t("LABEL_COMMENT") : t("LABEL_COMMENTS")} ·{" "}
          {saveCount} {saveCount === 1 ? t("LABEL_SAVE") : t("LABEL_SAVES")}
        </Text>
      </View>

      {/* Action buttons row — Save removed */}
      <View className="flex-row justify-between px-4 py-3 border-t border-ash-3 md:px-6">
        <LikeButton
          currentReaction={currentReaction}
          likeAnim={likeAnim}
          onPress={handleLikePress}
          onLongPress={() => {
            if (Platform.OS !== "web") setShowPicker(true);
          }}
          onHoverIn={() => {
            if (Platform.OS === "web") handleHoverIn();
          }}
          onHoverOut={() => {
            if (Platform.OS === "web") handleHoverOut();
          }}
        />

        <Pressable
          className="p-2 rounded hover:bg-ash-3 flex-row items-center gap-2"
          onPress={onComment}
        >
          <MessageText size={18} color={themeVars["--color-slate-default"] as string} variant="Outline" />
          <Text className="text-sm text-ash-2">{t("LABEL_COMMENT")}</Text>
        </Pressable>

        <Pressable
          className="p-2 rounded hover:bg-ash-3 flex-row items-center gap-2"
          onPress={onShare}
        >
          <ArrowForwardSquare
            size={18}
            color={themeVars["--color-slate-default"] as string}
            variant="Outline"
          />
          <Text className="text-sm text-ash-2">{t("LABEL_SHARE")}</Text>
        </Pressable>
      </View>

      {showPicker && (
        <ReactionPicker
          onSelect={handlePickerSelect}
          onClose={() => setShowPicker(false)}
          onStay={() => {
            if (hoverTimeout.current) {
              clearTimeout(hoverTimeout.current);
              hoverTimeout.current = null;
            }
          }}
        />
      )}
    </View>
  );
};

export default PostActions;
